import { motion } from "framer-motion";
import { Check, Gift } from "lucide-react";
import { BUNDLES, TOPUPS, BRAND } from "../../data/content";
import { Reveal } from "./Reveal";
import { BrandLabel } from "./BrandLabel";

const Bundle = ({ t, delay }) => (
  <motion.div
    data-testid={`pricing-card-${t.key}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`relative flex flex-col rounded-3xl p-7 transition-transform duration-500 hover:-translate-y-1 ${
      t.highlight
        ? "bg-ink text-white shadow-soft-lg lg:scale-[1.04]"
        : "bg-white border border-line text-ink shadow-soft"
    }`}
  >
    {t.badge && (
      <span
        className={`absolute -top-3 left-7 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] ${
          t.highlight ? "bg-food-accent text-white" : "bg-food-bg text-food-text"
        }`}
      >
        {t.badge}
      </span>
    )}

    <h3 className={`text-xl font-bold tracking-tight ${t.highlight ? "text-white" : "text-ink"}`}><BrandLabel text={t.name} /></h3>

    <div className="mt-6 flex items-end gap-1.5">
      <span className="text-4xl md:text-5xl font-bold tracking-tight">{t.price}</span>
      <span className={`mb-1.5 text-sm ${t.highlight ? "text-white/60" : "text-slate"}`}>
        {t.note}
      </span>
    </div>

    <div
      data-testid={`pricing-scans-${t.key}`}
      className={`mt-5 flex items-center gap-2.5 rounded-2xl px-4 py-3 ${
        t.highlight ? "bg-white/10" : "bg-food-bg"
      }`}
    >
      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-food-accent">
        <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.6} />
      </span>
      <span className="leading-tight">
        <span className={`block text-sm font-bold ${t.highlight ? "text-white" : "text-food-text"}`}>
          {t.scans}
        </span>
        {t.scansNote && (
          <span className={`block text-xs ${t.highlight ? "text-white/55" : "text-slate"}`}>
            {t.scansNote}
          </span>
        )}
      </span>
    </div>

    {t.items && (
      <ul className="mt-4 space-y-2.5">
        {t.items.map((it) => (
          <li
            key={it}
            className={`flex items-center gap-2 text-sm ${t.highlight ? "text-white/85" : "text-ink/75"}`}
          >
            <Check className="h-4 w-4 text-food-accent" strokeWidth={2.4} />
            <BrandLabel text={it} />
          </li>
        ))}
      </ul>
    )}

    <div className={`mt-4 flex items-center gap-2 text-sm ${t.highlight ? "text-white/80" : "text-ink/70"}`}>
      <Check className="h-4 w-4 text-food-accent" strokeWidth={2.4} />
      No recurring monthly subscription
    </div>

    <div className={`mt-2 flex items-center gap-2 text-sm ${t.highlight ? "text-white/80" : "text-ink/70"}`}>
      <Check className="h-4 w-4 text-food-accent" strokeWidth={2.4} />
      No expiry
    </div>

    <a
      data-testid={`pricing-select-${t.key}`}
      href={BRAND.appStoreUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`mt-7 w-full rounded-full py-3.5 text-center text-sm font-semibold transition-[transform,background-color] duration-300 hover:-translate-y-0.5 ${
        t.highlight ? "bg-white text-ink hover:bg-sand" : "bg-ink text-white hover:bg-[#1e293b]"
      }`}
    >
      Get {t.name}
    </a>
  </motion.div>
);

export const Pricing = ({ onPick }) => (
  <section id="pricing" className="relative py-20 md:py-28">
    <div className="mx-auto max-w-[1240px] px-5 md:px-8">
      <div className="max-w-2xl mx-auto text-center mb-14">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink leading-[1.1]">
            Transparent pricing
          </h2>
          <p className="mt-4 text-[15px] md:text-base text-slate leading-relaxed">
            Generous 1,000 'per scanner' scan allowances. Buy both scanners and save.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.05}>
        <div
          data-testid="risk-free-banner"
          className="mx-auto mb-10 flex max-w-3xl items-start gap-3 rounded-3xl border border-food-accent/25 bg-food-bg px-5 py-4 shadow-soft md:items-center"
        >
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-food-accent">
            <Gift className="h-4.5 w-4.5 text-white" strokeWidth={1.9} />
          </span>
          <p className="text-[15px] leading-relaxed text-food-text">
            <span className="font-bold">Try Before You Buy:</span> Includes
            4 FREE scans so you can test both scanners on your own groceries,
            toiletries, skincare and cosmetics before deciding if they are right for you.
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center max-w-6xl mx-auto">
        {BUNDLES.map((t, i) => (
          <Bundle key={t.key} t={t} delay={i * 0.1} />
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-8 max-w-3xl mx-auto rounded-3xl bg-white border border-line p-6 md:p-7 shadow-soft">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate mb-1">
              Scan top-up packs
            </p>
            <h3 className="text-lg font-bold tracking-tight text-ink">
              Need more scans? Top up anytime.
            </h3>
          </div>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {TOPUPS.map((p) => (
              <div
                key={p.key}
                data-testid={`topup-${p.key}`}
                className="rounded-2xl bg-page border border-line px-5 py-4"
              >
                <p className="text-sm font-semibold text-ink">{p.name}</p>
                <p className="mt-0.5 text-sm text-slate">{p.scans}</p>
                <p className="mt-2 text-xl font-bold tracking-tight text-food-text">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
